import { SvgIconComponent } from '@mui/icons-material'
import { ReactNode, forwardRef } from 'react'
import { IconProps } from '@mui/material'
import { LoadedTooltip } from '../display'
import { useBrowserDetection } from '$hooks'
import { Browser } from '$constants'

/**
 * A text input field with an inline icon to the right of the input content.
 * This is compatible with MUI TextField components and can be used as a
 * custom input component, ensuring normal MUI logic continues to work.
 */
export const InputWithInlineIcon = forwardRef<
  HTMLInputElement,
  InputWithInlineIconProps & { inputRef }
>(function InputWithInlineIcon(props) {
  const browser = useBrowserDetection()
  const { inputRef, message, Icon, iconProps, tabIndex, ...muiProps } = props

  return (
    <>
      <input ref={inputRef} tabIndex={tabIndex} {...muiProps} />
      {Icon && (
        <LoadedTooltip
          title={message}
          Icon={Icon}
          // disableInteractive here to stop a bug in the invoice grid lines where having multiple tooltips open
          // would cause the tooltip on the whole row of the grid to stay open when moving the mouse to another row.
          // The row Tooltip was added in LOADED-5855, if this tooltip has been removed
          // feel free to remove this prop.
          disableInteractive
          iconProps={{
            ...iconProps,
            sx: {
              color: (theme) => theme.palette.grey[600],
              fontSize: '1.7rem',
              marginLeft: '0.3lh',

              // Pull to the right the same amount as the icon width including
              // left margin, this ensure that an input in a centered column
              // that has an icon will not be off-center compared to any inputs
              // that don't have an icon.
              ...(browser !== Browser.Safari &&
                browser !== Browser.Firefox && {
                  marginRight: 'calc((1rem + 0.3lh) * -1)',
                }),

              // support overrides of the base styles
              ...iconProps?.sx,
            },
          }}
        />
      )}
    </>
  )
})

export type InlineInputIcon = {
  show: (value) => boolean
  Icon: SvgIconComponent | ((value) => SvgIconComponent | undefined) | undefined
  iconProps?: IconProps
  tooltipMessage: ReactNode | ((value) => ReactNode)
  tabIndex?: number
}

type InputWithInlineIconProps = {
  message: ReactNode
  Icon: SvgIconComponent
  iconProps?: IconProps
  tabIndex?: number
}
